import { AuthSession, AuthUser } from 'aws-amplify/auth';
import { Context, createContext, useContext } from 'react';

/**
 * Defines the structure of the authentication context value.
 *
 * @interface AuthenticationContextType
 * @property {AuthUser | undefined} user - The currently authenticated user.
 * @property {AuthSession | undefined} authSession - The current authentication session.
 */
interface AuthenticationContextType {
  user: AuthUser | undefined;
  authSession: AuthSession | undefined;
}

/**
 * Creates the authentication context with default values.
 *
 * @type {Context<AuthenticationContextType>}
 */
const AuthenticationContext: Context<AuthenticationContextType> =
  createContext<AuthenticationContextType>({
    user: undefined,
    authSession: undefined,
  });

/**
 * Custom hook to access the authentication context.
 *
 * @function useAuthentication
 * @returns {AuthenticationContextType} The authentication context value.
 *
 * @example
 * const { user, authSession } = useAuthentication();
 */
const useAuthentication = (): AuthenticationContextType => {
  const context = useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error('useAuthentication must be used within an AuthenticationContextProvider');
  }
  return context;
};

export { AuthenticationContext, useAuthentication };
